import { useState, type FormEvent } from "react";
import Select from "@/components/base/Select";
import { useToast } from "@/components/base/Toast";

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  dir?: "ltr";
  disabled?: boolean;
  hint?: string;
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  dir,
  disabled = false,
  hint,
}: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-800 mb-2">
        {label}
      </label>
      <input
        type={type}
        dir={dir}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className={`w-full h-11 rounded-btn border px-3 text-sm outline-none transition-colors duration-200 ${
          dir === "ltr" ? "text-left" : ""
        } ${
          disabled
            ? "bg-background-100 border-background-200 text-foreground-400 cursor-not-allowed"
            : "border-background-200 focus:ring-2 focus:ring-primary-300"
        }`}
      />
      {hint && <p className="mt-1.5 text-xs text-foreground-400">{hint}</p>}
    </div>
  );
}

function SaveBar({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 pt-1">
      <button
        type="submit"
        className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-primary-500 px-6 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-save-line"></i>
        حفظ التغييرات
      </button>
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center justify-center h-11 rounded-btn bg-background-100 px-6 text-sm font-semibold text-foreground-700 hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
      >
        استعادة
      </button>
    </div>
  );
}

export function PersonalForm() {
  const { show } = useToast();
  const initial = {
    fullName: "عبدالله المطيري",
    phone: "0551234567",
    email: "abdullah@yoursaudi.com",
    position: "المدير العام",
  };
  const [form, setForm] = useState(initial);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!form.fullName.trim()) {
      show("الاسم الكامل مطلوب", "error");
      return;
    }
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      show("صيغة البريد الإلكتروني غير صحيحة", "error");
      return;
    }
    show("تم حفظ البيانات الشخصية بنجاح");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field
        label="الاسم الكامل"
        value={form.fullName}
        onChange={(value) => setForm({ ...form, fullName: value })}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="رقم الجوال"
          value={form.phone}
          dir="ltr"
          type="tel"
          disabled
          onChange={() => {}}
          hint="لتغيير رقم الجوال تواصل مع الدعم"
        />
        <Field
          label="المسمى الوظيفي"
          value={form.position}
          onChange={(value) => setForm({ ...form, position: value })}
        />
      </div>
      <Field
        label="البريد الإلكتروني"
        value={form.email}
        dir="ltr"
        type="email"
        onChange={(value) => setForm({ ...form, email: value })}
      />
      <SaveBar
        onReset={() => {
          setForm(initial);
          show("تمت استعادة البيانات الأصلية", "warning");
        }}
      />
    </form>
  );
}

export function BankForm() {
  const { show } = useToast();
  const initial = {
    bankName: "مصرف الراجحي",
    accountHolder: "مطعم البيت السعودي",
    iban: "SA0380000000608010167519",
    accountNumber: "608010167519",
    cycle: "weekly",
  };
  const [form, setForm] = useState(initial);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!form.iban.trim() || !form.iban.trim().startsWith("SA")) {
      show("الرجاء إدخال رقم آيبان سعودي صحيح يبدأ بـ SA", "error");
      return;
    }
    show("تم حفظ البيانات البنكية بنجاح");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="اسم البنك"
          value={form.bankName}
          onChange={(value) => setForm({ ...form, bankName: value })}
        />
        <Field
          label="اسم صاحب الحساب"
          value={form.accountHolder}
          onChange={(value) => setForm({ ...form, accountHolder: value })}
        />
      </div>
      <Field
        label="رقم الآيبان (IBAN)"
        value={form.iban}
        dir="ltr"
        onChange={(value) => setForm({ ...form, iban: value })}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="رقم الحساب"
          value={form.accountNumber}
          dir="ltr"
          onChange={(value) => setForm({ ...form, accountNumber: value })}
        />
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-2">
            دورة التسوية
          </label>
          <Select
            value={form.cycle}
            options={[
              { value: "weekly", label: "كل 7 أيام" },
              { value: "biweekly", label: "كل 14 يومًا" },
              { value: "monthly", label: "شهريًا" },
            ]}
            onChange={(value) => setForm({ ...form, cycle: value })}
            ariaLabel="دورة التسوية"
          />
        </div>
      </div>
      <div className="rounded-btn bg-accent-50 border border-accent-200 px-4 py-3 text-xs leading-6 text-accent-900">
        تأكد من صحة بيانات الحساب البنكي، فسيتم تحويل مبالغ التسوية إليه تلقائيًا.
      </div>
      <SaveBar
        onReset={() => {
          setForm(initial);
          show("تمت استعادة البيانات الأصلية", "warning");
        }}
      />
    </form>
  );
}

export function SecurityForm() {
  const { show } = useToast();
  const [twoFactor, setTwoFactor] = useState(false);
  const [form, setForm] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [error, setError] = useState("");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!form.current) {
      setError("الرجاء إدخال كلمة المرور الحالية");
      return;
    }
    if (form.next.length < 6) {
      setError("كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    if (form.next !== form.confirm) {
      setError("كلمتا المرور غير متطابقتين");
      return;
    }
    setError("");
    setForm({ current: "", next: "", confirm: "" });
    show("تم تغيير كلمة المرور بنجاح");
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field
          label="كلمة المرور الحالية"
          value={form.current}
          type="password"
          onChange={(value) => {
            setForm({ ...form, current: value });
            setError("");
          }}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="كلمة المرور الجديدة"
            value={form.next}
            type="password"
            onChange={(value) => {
              setForm({ ...form, next: value });
              setError("");
            }}
          />
          <Field
            label="تأكيد كلمة المرور"
            value={form.confirm}
            type="password"
            onChange={(value) => {
              setForm({ ...form, confirm: value });
              setError("");
            }}
          />
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-primary-500 px-6 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-lock-password-line"></i>
          تحديث كلمة المرور
        </button>
      </form>

      <div className="border-t border-background-100 pt-5 space-y-3">
        <div className="flex items-center justify-between rounded-btn border border-background-200 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-foreground-900">
              التحقق بخطوتين
            </p>
            <p className="mt-0.5 text-xs text-foreground-500">
              حماية إضافية عند تسجيل الدخول عبر رمز يُرسل لجوالك
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={twoFactor}
            onClick={() => {
              setTwoFactor((prev) => !prev);
              show(twoFactor ? "تم إيقاف التحقق بخطوتين" : "تم تفعيل التحقق بخطوتين", twoFactor ? "warning" : "success");
            }}
            className={`relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer ${
              twoFactor ? "bg-primary-500" : "bg-background-300"
            }`}
            aria-label="تفعيل التحقق بخطوتين"
          >
            <span
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200 ${
                twoFactor ? "start-5" : "start-0.5"
              }`}
            ></span>
          </button>
        </div>

        <button
          type="button"
          onClick={() => show("تم تسجيل الخروج من كل الأجهزة الأخرى", "warning")}
          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-btn border border-red-200 bg-red-50 text-sm font-semibold text-red-600 hover:bg-red-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-logout-box-r-line"></i>
          تسجيل الخروج من كل الأجهزة
        </button>
      </div>
    </div>
  );
}